import type { SharedData, UserRole } from "@src/context/ApplicationContext";
import type { CanvasEvent } from "@src/interfaces/canvas.interface";
import { CANVAS_PREFILL_EVENT_NAME } from "./constants";
import { mapCanvasEventPayloadToSharedDataPatch } from "./event-prefill-mapper";
import type { CanvasTrackingState, SalesforceCanvasPrefillPayload } from "./types";

export { CANVAS_PREFILL_EVENT_NAME } from "./constants";

export const INITIAL_CANVAS_TRACKING: CanvasTrackingState = {
  leadId: "",
  eventKey: "",
  version: 0,
  appliedAt: "",
};

export type CanvasPrefillIgnoredReason = "not-target-event" | "duplicate-event" | "empty-patch";

export type CanvasPrefillApplicationResult =
  | {
      status: "ignored";
      reason: CanvasPrefillIgnoredReason;
    }
  | {
      status: "apply";
      patch: Partial<SharedData>;
      nextTracking: CanvasTrackingState;
      shouldResetSession: boolean;
    };

/** Fields that must be blanked when the Canvas payload indicates self-pay,
 *  so stale insurance data from a previous prefill doesn't leak through. */
const SELF_PAY_INSURANCE_CLEAR_PATCH: Partial<SharedData> = {
  selectedPolicy: null,
  insurancePolicy: null,
  isPrimaryPolicyHolder: undefined,
  memberId: "",
  policyholderFirstName: "",
  policyholderLastName: "",
  policyholderDOB: "",
  policyholderGender: "",
  relationshipToPolicyholder: "",
};

const sortObjectKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortObjectKeys);
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  return Object.keys(value as Record<string, unknown>)
    .sort((a, b) => {
      return a.localeCompare(b);
    })
    .reduce<Record<string, unknown>>((accumulator, key) => {
      accumulator[key] = sortObjectKeys((value as Record<string, unknown>)[key]);
      return accumulator;
    }, {});
};

/**
 * Builds a deterministic string key from the event name + payload so we
 * can detect duplicate events (same data arriving twice).
 * Object keys are sorted to ensure identical payloads always hash the same.
 */
const createCanvasPrefillEventKey = (canvasEvent: CanvasEvent): string => {
  return `${canvasEvent.name}:${JSON.stringify(sortObjectKeys(canvasEvent.payload))}`;
};

const getLeadId = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== "object") {
    return undefined;
  }

  const { patient } = payload as SalesforceCanvasPrefillPayload;
  return typeof patient?.leadId === "string" && patient.leadId.trim()
    ? patient.leadId.trim()
    : undefined;
};

const hasMappedFields = (patch: Partial<SharedData>): boolean => {
  return Object.keys(patch).length > 0;
};

/**
 * Pure decision function: given a Canvas event and the current tracking
 * state, determines whether to apply the prefill and what the resulting
 * SharedData patch + next tracking state should be.
 *
 * Returns `{ status: "ignored" }` when the event is not actionable
 * (wrong name, duplicate payload, or no mappable fields).
 *
 * Returns `{ status: "apply", patch, nextTracking, shouldResetSession }`
 * when the event should be applied.  `shouldResetSession` is true when the
 * incoming lead differs from the previously applied lead.
 */
export function buildCanvasPrefillApplicationPatch(
  canvasEvent: CanvasEvent | null | undefined,
  tracking: CanvasTrackingState,
  userRole?: UserRole,
  getTimestamp: () => string = () => {
    return new Date().toISOString();
  },
): CanvasPrefillApplicationResult {
  if (canvasEvent?.name !== CANVAS_PREFILL_EVENT_NAME) {
    return { status: "ignored", reason: "not-target-event" };
  }

  const eventKey = createCanvasPrefillEventKey(canvasEvent);
  if (tracking.eventKey === eventKey) {
    return { status: "ignored", reason: "duplicate-event" };
  }

  const mappedPatch = mapCanvasEventPayloadToSharedDataPatch(canvasEvent.payload, userRole);
  if (!hasMappedFields(mappedPatch)) {
    return { status: "ignored", reason: "empty-patch" };
  }

  const leadId = getLeadId(canvasEvent.payload);
  const shouldResetSession = Boolean(leadId && (!tracking.leadId || tracking.leadId !== leadId));

  const nextVersion = tracking.version + 1;

  return {
    status: "apply",
    shouldResetSession,
    patch: {
      ...mappedPatch,
      ...(mappedPatch.paymentMethod === "self_pay" ? SELF_PAY_INSURANCE_CLEAR_PATCH : {}),
      canvasPrefillVersion: nextVersion,
    },
    nextTracking: {
      leadId: leadId ?? tracking.leadId,
      eventKey,
      version: nextVersion,
      appliedAt: getTimestamp(),
    },
  };
}
